import { genericToolTip, hasDiagramDarkMode } from '@y42/gojs';
import { variableColors } from '@y42/model';
import * as go from 'gojs';
import { OrchestrationNodeBindings, OrchestrationUINode } from '../../../../../models/orchestration-diagram.model';
import { OrchestrationDiagramParts } from '../../../../config/orchestration-part-names';
import { visibleInOverview, visibleInStructure } from '../../utils/bindings';
import { gojsJobIcon } from '../import-icon';

const $ = go.GraphObject.make;

const config = {
  colors: {
    light: {
      fill: variableColors.gray200,
      fillOver: variableColors.gray300,
      fillPressed: variableColors.gray400,
    },
    dark: {
      fill: variableColors.gray300Dark,
      fillOver: variableColors.gray400Dark,
      fillPressed: variableColors.gray100Dark,
    },
  },
};

export const ImportTypePlaceholder = (props: Record<string, unknown> = {}, bindings: go.Binding[] = []) =>
  $(
    go.Panel,
    go.Panel.Horizontal,
    { ...props },
    ...bindings,
    ImportTypeButton({}, [
      visibleInStructure(),
      isEnabledOnAllowedIncremental(),
      importBtnFill(),
      importBtnFillPressed(),
    ]),
    gojsJobIcon({ toolTip: ToolTipInOverview() }, [visibleInOverview()]),
  );

const ImportTypeButton = (props: Record<string, unknown> = {}, bindings: go.Binding[] = []) =>
  $(
    'Button',
    {
      name: OrchestrationDiagramParts.ImportTypeButton,
      'ButtonBorder.strokeWidth': 0,
      _buttonFillOver: 'transparent',
      width: 30,
      height: 30,
      toolTip: ToolTipInStructure(),
      ...props,
    },
    ...bindings,

    gojsJobIcon(),
  );

const importBtnFill = () =>
  new go.Binding('ButtonBorder.fill', '', (__node: OrchestrationUINode, part: go.Part) => {
    const hasDarkMode = hasDiagramDarkMode(part.diagram);

    return hasDarkMode ? config.colors.dark.fill : config.colors.light.fill;
  }).ofObject();

const importBtnFillPressed = () =>
  new go.Binding('_buttonFillPressed', '', (__node: OrchestrationUINode, part: go.Part) => {
    const hasDarkMode = hasDiagramDarkMode(part.diagram);

    return hasDarkMode ? config.colors.dark.fillPressed : config.colors.light.fillPressed;
  }).ofObject();

const ToolTipInStructure = () =>
  genericToolTip({ colors: { fill: variableColors.white, stroke: variableColors.gray800 } }, [
    new go.Binding('text', '', (node: OrchestrationUINode) => {
      const hasIncrementalImportType = !!node[OrchestrationNodeBindings.NodeConfig]?.incremental;
      const allowImportTypeToggle = !!node[OrchestrationNodeBindings.AllowIncremental];

      if (!allowImportTypeToggle) {
        return staticTooltipText(hasIncrementalImportType);
      }
      return dynamicTooltipText(hasIncrementalImportType);
    }),
  ]);

const ToolTipInOverview = () =>
  genericToolTip({ colors: { fill: variableColors.white, stroke: variableColors.gray800 } }, [
    new go.Binding('text', '', (node: OrchestrationUINode) =>
      staticTooltipText(!!node[OrchestrationNodeBindings.NodeConfig]?.incremental),
    ),
  ]);

const staticTooltipText = (hasIncrementalImportType: boolean) =>
  `${hasIncrementalImportType ? 'Incremental' : 'Full'} Import`;

// Used for import type toggle button
const dynamicTooltipText = (hasIncrementalImportType: boolean) =>
  hasIncrementalImportType ? 'Incremental: Switch to Full Import' : 'Full: Switch to Incremental Import';

// Bindings
const isEnabledOnAllowedIncremental = () =>
  new go.Binding('isEnabled', '', (node: OrchestrationUINode) => node[OrchestrationNodeBindings.AllowIncremental]);
